import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Inbox, RefreshCw, RotateCcw, UserPlus, Hand } from "lucide-react";
import { Select } from "antd";
import { ContainerCard } from "../../components/ui/cards";
import { DialogModal } from "../../components/ui/dialogModal";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import {
    ASSIGNMENT_ASSIGN_URL, ASSIGNMENT_CAPACITY_URL, ASSIGNMENT_CLAIM_URL, ASSIGNMENT_RETRY_URL,
    ASSIGNMENT_SETTINGS_URL, ASSIGNMENT_UNASSIGNED_URL,
} from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { formatDateTime } from "../../utils/datetime";
import { Pill, STATUS_META, Switch, btnOutline, btnPrimary, inputCls, minutesLabel } from "../../components/assignment/shared";

/**
 * Leads -> Unassigned: every open lead with no owner (or an owner who has
 * left), oldest first, with the reason the engine gave. Managers assign or
 * retry; reps can take a lead themselves when the setting allows.
 */
const AGE = [["", "Any age"], ["15m", "Over 15 min"], ["1h", "Over 1 hour"], ["24h", "Over 24 hours"]];

export default function UnassignedLeads() {
    const { fetch, create, update } = useApiMethods();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [filters, setFilters] = useState({ reason_code: "", age: "", search: "", failed_only: "" });
    const [page, setPage] = useState(1);
    const [selected, setSelected] = useState([]);
    const [users, setUsers] = useState([]);
    const [teams, setTeams] = useState([]);
    const [assignOpen, setAssignOpen] = useState(false);
    const [assignTo, setAssignTo] = useState({ mode: "user", user_id: null, team_id: null, reason: "" });
    const [busy, setBusy] = useState(false);
    const [me, setMe] = useState(null);

    const load = (pg = page) => fetch(ASSIGNMENT_UNASSIGNED_URL, { ...filters, page: pg, page_size: 50 }).then(res => {
        if (res?.status === "success") { setData(res); setPage(res.page); }
        else setData({ results: [], error: res?.message || "Could not load the queue" });
    });

    useEffect(() => { load(1); }, [filters.reason_code, filters.age, filters.failed_only]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetch(ASSIGNMENT_SETTINGS_URL).then(res => {
            if (res?.status === "success") { setUsers(res.users || []); setTeams((res.teams || []).filter(t => t.is_active)); }
        });
        fetch(ASSIGNMENT_CAPACITY_URL, { me: 1 }).then(res => { if (res?.status === "success") setMe(res.people?.[0]); });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const rows = data?.results || [];
    const allChecked = rows.length > 0 && rows.every(r => selected.includes(r.lead_id));
    const toggleAll = () => setSelected(allChecked ? [] : rows.map(r => r.lead_id));
    const toggle = id => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

    const doAssign = async () => {
        if (assignTo.mode === "user" && !assignTo.user_id) { ToastMsg("error", "Pick a person"); return; }
        if (assignTo.mode === "team" && !assignTo.team_id) { ToastMsg("error", "Pick a team"); return; }
        setBusy(true);
        try {
            const res = await create(ASSIGNMENT_ASSIGN_URL, { lead_ids: selected, ...assignTo });
            if (res?.status === "success") {
                ToastMsg(res.skipped ? "warning" : "success", res.message);
                setAssignOpen(false); setSelected([]); load();
            } else ToastMsg("error", res?.message || "Could not assign");
        } finally { setBusy(false); }
    };

    const retry = async (ids) => {
        setBusy(true);
        try {
            const res = await create(ASSIGNMENT_RETRY_URL, { lead_ids: ids });
            if (res?.status === "success") { ToastMsg("success", res.message); setSelected([]); load(); }
            else ToastMsg("error", res?.message || "Could not retry");
        } finally { setBusy(false); }
    };

    const claim = async (id) => {
        const res = await create(`${ASSIGNMENT_CLAIM_URL}${id}/`, {});
        if (res?.status === "success") { ToastMsg("success", res.message); load(); }
        else ToastMsg("error", res?.message || "Could not take the lead");
    };

    const saveMe = async (patch) => {
        const res = await update(`${ASSIGNMENT_CAPACITY_URL}${me.user_id}/`, patch);
        if (res?.status === "success") { setMe(res.person); ToastMsg("success", "Saved"); }
        else ToastMsg("error", res?.message || "Could not save");
    };

    const reasonLabel = code => (data?.reason_labels || {})[code] || code;

    return (
        <ContainerCard>
            <div className="p-4 space-y-4 overflow-auto">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <Inbox className="h-5 w-5 text-primary" />
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Unassigned leads {data?.count != null && <span className="text-sm font-normal text-gray-500">· {data.count}</span>}
                            </h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                Open leads with no owner, oldest first.
                                {data && !data.engine_on && " Automatic assignment is off — turn it on in Settings → Lead Assignment."}
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {me && (
                            <div className="flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 px-2.5 py-1 text-xs">
                                <span className="text-gray-600 dark:text-gray-300">I'm receiving leads</span>
                                <Switch checked={me.receiving_leads} onChange={v => saveMe({ receiving_leads: v })} label="Receiving leads" />
                                <Pill className={(STATUS_META[me.status] || STATUS_META.off).cls}>{(STATUS_META[me.status] || STATUS_META.off).label}</Pill>
                            </div>
                        )}
                        <button type="button" className={btnOutline} onClick={() => load()}><RefreshCw className="h-3.5 w-3.5" /> Refresh</button>
                    </div>
                </div>

                {(data?.reason_counts || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                        {data.reason_counts.map(r => (
                            <button key={r.reason_code} type="button"
                                onClick={() => setFilters(f => ({ ...f, reason_code: f.reason_code === r.reason_code ? "" : r.reason_code }))}
                                className={cn("px-2 py-1 rounded-full text-[11px] border",
                                    filters.reason_code === r.reason_code ? "border-primary bg-brand-50 text-primary dark:bg-brand-900/20"
                                        : "border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300")}>
                                {reasonLabel(r.reason_code)} · {r.n}
                            </button>
                        ))}
                    </div>
                )}

                <div className="flex flex-wrap items-center gap-2">
                    <input value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
                        onKeyDown={e => e.key === "Enter" && load(1)} placeholder="Name or phone…" className={cn(inputCls, "w-48")} />
                    <select value={filters.age} onChange={e => setFilters(f => ({ ...f, age: e.target.value }))} className={cn(inputCls, "w-36")}>
                        {AGE.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                    <label className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-300">
                        <input type="checkbox" checked={!!filters.failed_only}
                            onChange={e => setFilters(f => ({ ...f, failed_only: e.target.checked ? "1" : "" }))} /> Failed only
                    </label>
                    {data?.can_assign && selected.length > 0 && (
                        <div className="flex gap-2 ml-auto">
                            <button type="button" className={btnOutline} disabled={busy || !data.engine_on} onClick={() => retry(selected)}>
                                <RotateCcw className="h-3.5 w-3.5" /> Retry automatic ({selected.length})
                            </button>
                            <button type="button" className={btnPrimary} onClick={() => setAssignOpen(true)}>
                                <UserPlus className="h-3.5 w-3.5" /> Assign ({selected.length})
                            </button>
                        </div>
                    )}
                </div>

                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                    <table className="min-w-full text-xs">
                        <thead className="bg-gray-50 dark:bg-gray-700/40 text-gray-500">
                            <tr>
                                {data?.can_assign && <th className="px-3 py-2 w-8"><input type="checkbox" checked={allChecked} onChange={toggleAll} aria-label="Select all" /></th>}
                                {["Lead", "Source", "Project", "State", "Waiting", "Why it is here", "Tries", ""].map(h =>
                                    <th key={h} className="px-3 py-2 text-left font-medium whitespace-nowrap">{h}</th>)}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {rows.map(r => (
                                <tr key={r.lead_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                                    {data.can_assign && <td className="px-3 py-2"><input type="checkbox" checked={selected.includes(r.lead_id)} onChange={() => toggle(r.lead_id)} aria-label="Select lead" /></td>}
                                    <td className="px-3 py-2">
                                        <button type="button" className="text-primary hover:underline font-medium text-left"
                                            onClick={() => navigate(`/customers/customerdetails?id=${r.lead_id}`, { state: { formEdit: true, row: { id: r.lead_id } } })}>
                                            {r.name || `Lead #${r.lead_id}`}
                                        </button>
                                        <p className="text-[10px] text-gray-400">{r.contact_name}{r.phone ? ` · ${r.phone}` : ""}</p>
                                    </td>
                                    <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{r.source || "—"}</td>
                                    <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{r.project || "—"}</td>
                                    <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{r.state || r.city || "—"}</td>
                                    <td className={cn("px-3 py-2 whitespace-nowrap tabular-nums", r.waiting_minutes > 60 ? "text-red-600" : r.waiting_minutes > 15 ? "text-amber-600" : "text-gray-600")}
                                        title={formatDateTime(r.created_date)}>{minutesLabel(r.waiting_minutes)}</td>
                                    <td className="px-3 py-2 text-gray-600 dark:text-gray-300 max-w-sm">
                                        <span className={cn(r.request_status === "failed" && "text-red-600")}>{r.reason}</span>
                                        {r.former_owner && <span className="block text-[10px] text-gray-400">Was {r.former_owner}</span>}
                                    </td>
                                    <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{r.attempts || 0}
                                        {r.next_retry_at && <span className="block text-[10px] text-gray-400">next {formatDateTime(r.next_retry_at)}</span>}</td>
                                    <td className="px-3 py-2 text-right whitespace-nowrap">
                                        {data.can_claim && (
                                            <button type="button" onClick={() => claim(r.lead_id)}
                                                className="text-[11px] text-primary hover:underline inline-flex items-center gap-0.5 mr-2">
                                                <Hand className="h-3 w-3" /> Take it
                                            </button>
                                        )}
                                        {data.can_assign && data.engine_on && (
                                            <button type="button" onClick={() => retry([r.lead_id])} disabled={busy}
                                                className="text-[11px] text-gray-500 hover:text-primary inline-flex items-center gap-0.5">
                                                <RotateCcw className="h-3 w-3" /> Retry
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {data && !rows.length && (
                                <tr><td colSpan={9} className="px-3 py-10 text-center text-gray-400">{data.error || "All leads have an owner."}</td></tr>
                            )}
                            {!data && <tr><td colSpan={9} className="px-3 py-10 text-center text-gray-400">Loading…</td></tr>}
                        </tbody>
                    </table>
                </div>
                {data?.num_pages > 1 && (
                    <div className="flex items-center justify-end gap-2 text-xs">
                        <button disabled={page <= 1} onClick={() => load(page - 1)} className="px-2 py-1 rounded border border-gray-200 disabled:opacity-40">Previous</button>
                        <span className="text-gray-500">Page {page} of {data.num_pages}</span>
                        <button disabled={page >= data.num_pages} onClick={() => load(page + 1)} className="px-2 py-1 rounded border border-gray-200 disabled:opacity-40">Next</button>
                    </div>
                )}
            </div>

            <DialogModal open={assignOpen} onClose={() => setAssignOpen(false)} onSave={doAssign} loading={busy}
                title={`Assign ${selected.length} lead${selected.length === 1 ? "" : "s"}`} saveLabel="Assign" closeLabel="Cancel" width="480px">
                <AssignForm value={assignTo} onChange={setAssignTo} users={users} teams={teams} />
            </DialogModal>
        </ContainerCard>
    );
}

export function AssignForm({ value, onChange, users, teams, requireReason }) {
    return (
        <div className="space-y-3 p-1">
            <div className="flex gap-4 text-xs">
                {[["user", "To one person"], ["team", "Share across a team"]].map(([k, l]) => (
                    <label key={k} className="flex items-center gap-1 text-gray-700 dark:text-gray-200">
                        <input type="radio" checked={value.mode === k} onChange={() => onChange({ ...value, mode: k })} /> {l}
                    </label>
                ))}
            </div>
            {value.mode === "user" ? (
                <Select showSearch className="w-full" size="large" placeholder="Pick a person"
                    value={value.user_id || undefined} onChange={v => onChange({ ...value, user_id: v })}
                    options={users.map(u => ({ value: u.id, label: u.name }))}
                    filterOption={(input, o) => (o?.label || "").toLowerCase().includes(input.toLowerCase())} />
            ) : (
                <>
                    <select value={value.team_id || ""} onChange={e => onChange({ ...value, team_id: e.target.value ? Number(e.target.value) : null })}
                        className={inputCls}>
                        <option value="">Pick a team…</option>
                        {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                    <p className="text-[11px] text-gray-500">Shared in turn, within each rep's limits. Leads nobody can take stay where they are.</p>
                </>
            )}
            <input value={value.reason || ""} onChange={e => onChange({ ...value, reason: e.target.value })} maxLength={255}
                placeholder={requireReason ? "Reason (required)" : "Reason (optional)"} className={inputCls} />
        </div>
    );
}
